import {
  ITransportAdapter,
  RealtimeListener,
  RealtimePayload,
  RealtimeStatus,
} from "./types";

export interface FastApiAdapterOptions {
  baseUrl?: string;
  httpUrl?: string;
  tokenGetter?: () => Promise<string | null>;
  onError?: (error: Error) => void;
  pingIntervalMs?: number;
}

export class FastApiWebSocketAdapter implements ITransportAdapter {
  private baseUrl: string;
  private httpUrl: string;
  private tokenGetter: (() => Promise<string | null>) | null = null;
  private websockets: Map<string, WebSocket> = new Map();
  private channelRefCount: Map<string, number> = new Map();
  private identifier: string | null = null;
  private subscribedChannels: Set<string> = new Set();
  private listeners: Set<RealtimeListener> = new Set();
  private status: RealtimeStatus = "disconnected";
  private reconnectTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private pingIntervals: Map<string, NodeJS.Timeout> = new Map();
  private options?: FastApiAdapterOptions;

  constructor(options?: FastApiAdapterOptions) {
    this.options = options;
    const defaultWsUrl =
      typeof window !== "undefined" && window.location.protocol === "https:"
        ? "wss://api.peerbots.org"
        : "ws://localhost:8000";
    const defaultHttpUrl =
      typeof window !== "undefined" && window.location.protocol === "https:"
        ? "https://api.peerbots.org"
        : "http://localhost:8000";

    const envWsUrl =
      (typeof process !== "undefined" && process.env?.VITE_WS_URL) ||
      (typeof import.meta !== "undefined" && import.meta.env?.VITE_WS_URL);
    const envHttpUrl =
      (typeof process !== "undefined" && process.env?.VITE_API_URL) ||
      (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL);

    this.baseUrl = options?.baseUrl || envWsUrl || defaultWsUrl;
    this.httpUrl = options?.httpUrl || envHttpUrl || defaultHttpUrl;
    if (options?.tokenGetter) {
      this.tokenGetter = options.tokenGetter;
    }
  }

  setIdentifier(id: string): void {
    this.identifier = id;
  }

  setTokenGetter(getter: () => Promise<string | null>): void {
    this.tokenGetter = getter;
    // Reconnect any subscribed channels that failed or are closed now that token is available
    for (const channel of this.subscribedChannels) {
      const ws = this.websockets.get(channel);
      if (
        !ws ||
        ws.readyState === WebSocket.CLOSED ||
        ws.readyState === WebSocket.CLOSING
      ) {
        this._reconnectChannel(channel);
      }
    }
  }

  async subscribe(channels: string[]): Promise<void> {
    const token = this.tokenGetter ? await this.tokenGetter() : null;

    for (const rawChannel of channels) {
      const channel = rawChannel.toLowerCase();
      const count = (this.channelRefCount.get(channel) || 0) + 1;
      this.channelRefCount.set(channel, count);
      this.subscribedChannels.add(channel);
      if (this.websockets.has(channel)) continue;

      let wsUrl = `${this.baseUrl}/v1/ws/${encodeURIComponent(channel)}`;
      const params = new URLSearchParams();
      if (token) params.set("token", token);
      if (this.identifier) params.set("client_id", this.identifier);
      const queryString = params.toString();
      if (queryString) {
        wsUrl += `?${queryString}`;
      }

      this._connectChannel(channel, wsUrl);
    }
  }

  private async _reconnectChannel(channel: string): Promise<void> {
    if (!this.subscribedChannels.has(channel)) return;
    const token = this.tokenGetter ? await this.tokenGetter() : null;
    let wsUrl = `${this.baseUrl}/v1/ws/${encodeURIComponent(channel)}`;
    const params = new URLSearchParams();
    if (token) params.set("token", token);
    if (this.identifier) params.set("client_id", this.identifier);
    const queryString = params.toString();
    if (queryString) {
      wsUrl += `?${queryString}`;
    }
    this._connectChannel(channel, wsUrl);
  }

  private _connectChannel(channel: string, wsUrl: string): void {
    if (typeof WebSocket === "undefined") return;

    console.debug(
      `FastApiWebSocketAdapter: connecting to channel ${channel} (${wsUrl})`,
    );
    this._updateStatus("connecting");
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.debug(`FastApiWebSocketAdapter: connected to channel ${channel}`);
      this._updateStatus("connected");

      // Start periodic ping heartbeat to keep proxies and firewalls from dropping idle streams
      const existingPing = this.pingIntervals.get(channel);
      if (existingPing) {
        clearInterval(existingPing);
      }
      const pingMs = this.options?.pingIntervalMs ?? 20000;
      if (pingMs > 0) {
        const pingInterval = setInterval(() => {
          if (
            ws.readyState === 1 ||
            (typeof WebSocket !== "undefined" &&
              ws.readyState === WebSocket.OPEN)
          ) {
            try {
              ws.send(JSON.stringify({ type: "ping" }));
            } catch (err) {
              console.warn(
                `FastApiWebSocketAdapter: ping failed for channel ${channel}`,
                err,
              );
            }
          }
        }, pingMs);
        this.pingIntervals.set(channel, pingInterval);
      }
    };

    ws.onmessage = (event: { data: string }) => {
      try {
        const parsed = JSON.parse(event.data);
        console.debug(
          `FastApiWebSocketAdapter: message on ${channel}:`,
          parsed,
        );

        // Filter out heartbeat pong responses from the server
        if (parsed.type === "pong") {
          return;
        }

        if (parsed.type === "_presence") {
          for (const listener of this.listeners) {
            listener.onPresence?.({
              action: parsed.action,
              uuid: parsed.uuid,
              channel: parsed.channel || channel,
              timestamp: parsed.timestamp || Date.now(),
            });
          }
          return;
        }

        for (const listener of this.listeners) {
          listener.onMessage?.(parsed as RealtimePayload, channel);
        }
      } catch (e) {
        console.error("FastApiWebSocketAdapter: message error", e);
      }
    };

    ws.onerror = (e) => {
      console.error(`FastApiWebSocketAdapter: error on channel ${channel}`, {
        channel,
        wsUrl,
        readyState: ws.readyState,
        protocol: ws.protocol,
        bufferedAmount: ws.bufferedAmount,
        error: e,
      });
      const ping = this.pingIntervals.get(channel);
      if (ping) {
        clearInterval(ping);
        this.pingIntervals.delete(channel);
      }
      this._updateStatus("error");
      if (this.options?.onError) {
        this.options.onError(
          new Error(`WebSocket connection to channel ${channel} failed`),
        );
      }
    };

    ws.onclose = (e) => {
      console.warn(`FastApiWebSocketAdapter: closed on channel ${channel}`, {
        channel,
        code: (e as CloseEvent)?.code,
        reason: (e as CloseEvent)?.reason,
        wasClean: (e as CloseEvent)?.wasClean,
        readyState: ws.readyState,
        wsUrl,
      });
      const ping = this.pingIntervals.get(channel);
      if (ping) {
        clearInterval(ping);
        this.pingIntervals.delete(channel);
      }
      this.websockets.delete(channel);
      if (this.subscribedChannels.has(channel)) {
        this._updateStatus("disconnected");
        // Schedule auto-reconnection with backoff
        const timeout = setTimeout(() => {
          if (this.subscribedChannels.has(channel)) {
            this._reconnectChannel(channel);
          }
        }, 2000);
        this.reconnectTimeouts.set(channel, timeout);
      }
    };

    this.websockets.set(channel, ws);
  }

  unsubscribe(channels: string[]): void {
    for (const rawChannel of channels) {
      const channel = rawChannel.toLowerCase();
      const count = (this.channelRefCount.get(channel) || 1) - 1;
      if (count <= 0) {
        this.channelRefCount.delete(channel);
        this.subscribedChannels.delete(channel);
        const timer = this.reconnectTimeouts.get(channel);
        if (timer) {
          clearTimeout(timer);
          this.reconnectTimeouts.delete(channel);
        }
        const ping = this.pingIntervals.get(channel);
        if (ping) {
          clearInterval(ping);
          this.pingIntervals.delete(channel);
        }
        const ws = this.websockets.get(channel);
        if (ws) {
          ws.onopen = null;
          ws.onmessage = null;
          ws.onerror = null;
          ws.onclose = null;
          try {
            ws.close();
          } catch {
            // Ignore close errors on already closed socket
          }
          this.websockets.delete(channel);
        }
      } else {
        this.channelRefCount.set(channel, count);
      }
    }
    if (this.subscribedChannels.size === 0) {
      this._updateStatus("disconnected");
    }
  }

  async publish(channel: string, payload: RealtimePayload): Promise<void> {
    const ch = channel.toLowerCase();
    const ws = this.websockets.get(ch);
    const messageStr =
      typeof payload === "string" ? payload : JSON.stringify(payload);

    // If socket is currently connecting (readyState === 0), wait up to 1500ms for it to become OPEN (1)
    const isConnecting =
      ws &&
      (ws.readyState === 0 ||
        (typeof WebSocket !== "undefined" &&
          ws.readyState === WebSocket.CONNECTING));
    if (isConnecting) {
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, 1500);
        const onDone = () => {
          clearTimeout(timer);
          resolve();
        };
        if (typeof ws.addEventListener === "function") {
          ws.addEventListener("open", onDone, { once: true });
          ws.addEventListener("close", onDone, { once: true });
          ws.addEventListener("error", onDone, { once: true });
        } else {
          const origOpen = ws.onopen;
          ws.onopen = (ev) => {
            clearTimeout(timer);
            origOpen?.call(ws, ev);
            resolve();
          };
        }
      });
    }

    const isOpen =
      ws &&
      (ws.readyState === 1 ||
        (typeof WebSocket !== "undefined" && ws.readyState === WebSocket.OPEN));
    if (isOpen) {
      ws.send(messageStr);
      return;
    }

    // Fallback to REST publish
    const token = this.tokenGetter ? await this.tokenGetter() : null;
    if (!token && ch.startsWith("guest")) {
      console.debug(
        `FastApiWebSocketAdapter: WebSocket not open for guest channel ${ch} and unauthenticated; skipping REST fallback`,
      );
      return;
    }

    console.warn(
      `FastApiWebSocketAdapter: WebSocket not open (readyState=${ws?.readyState}). Publishing via REST fallback to ${ch}:`,
      payload,
    );
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.httpUrl}/v1/publish`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          channel: ch,
          message: payload,
        }),
      });

      if (!response.ok) {
        if (response.status === 401 && ch.startsWith("guest")) {
          console.debug(
            `FastApiWebSocketAdapter: REST publish not permitted for anonymous guest channel ${ch}`,
          );
        } else {
          console.error(
            "FastApiWebSocketAdapter: REST publish failed",
            response.statusText,
          );
        }
      }
    } catch (e) {
      console.error("FastApiWebSocketAdapter: REST publish error", e);
    }
  }

  addListener(listener: RealtimeListener): void {
    this.listeners.add(listener);
  }

  removeListener(listener: RealtimeListener): void {
    this.listeners.delete(listener);
  }

  async getPresence(channel: string): Promise<string[]> {
    try {
      const token = this.tokenGetter ? await this.tokenGetter() : null;
      if (!token && channel.toLowerCase().startsWith("guest")) {
        return [];
      }
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const response = await fetch(
        `${this.httpUrl}/v1/channels/${encodeURIComponent(channel)}/presence`,
        { headers },
      );
      if (response.ok) {
        const data = await response.json();
        return data.occupants || [];
      }
      return [];
    } catch (e) {
      console.debug("FastApiWebSocketAdapter: getPresence failed", e);
      return [];
    }
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  disconnect(): void {
    for (const [, timer] of this.reconnectTimeouts) {
      clearTimeout(timer);
    }
    this.reconnectTimeouts.clear();

    for (const [, ping] of this.pingIntervals) {
      clearInterval(ping);
    }
    this.pingIntervals.clear();

    for (const [, ws] of this.websockets) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        ws.close();
      } catch {
        // Ignore close errors on already closed socket
      }
    }
    this.websockets.clear();
    this.channelRefCount.clear();
    this.subscribedChannels.clear();
    this.listeners.clear();
    this._updateStatus("disconnected");
  }

  private _updateStatus(newStatus: RealtimeStatus) {
    this.status = newStatus;
    for (const listener of this.listeners) {
      listener.onStatus?.(newStatus);
    }
  }
}
